import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { env } from '../lib/config/env'
import { getLanguageFromStorage } from '../lib/getLanguageFromStorage'
import {
    ACCREDITATION_TAG,
    APPLICATION_COMPETITION_TAG,
    CHILD_MATCHES_TAG,
    CLIENT_TAG,
    COMPETITION_DOCUMENTS_TAG,
    COMPETITION_GROUP_STATS_TAG,
    COMPETITION_TABLE_STATS_TAG,
    COMPETITION_TAG,
    DISCIPLINE_TAG,
    EVENT_TAG,
    FORMATION_TAG,
    GENERAL_APPLICATION,
    GROUP_MATCHES_TAG,
    GROUP_TAG,
    INDIVIDUAL_USER,
    LEAGUES_TAG,
    LEAGUE_TAG,
    MATCHES_TAG,
    MATCH_TAG,
    MEDICAL_EXAM_TAG,
    ORGANIZATION_TAG,
    ORGANIZATION_UNIT_CONTACTS_TAG,
    ORGANIZATION_UNIT_OPTIONS,
    ORGANIZATION_UNIT_TAG,
    PHASES_TAG,
    POSITION_TAG,
    QUICK_SELECTS_TAG,
    RACES_TAG,
    RACE_STATS_TAG,
    RECREATIONS_TAG,
    RECREATION_INSTANCE_ATTENDANCE_OF_A_RECREATION_TAG,
    RECREATION_INSTANCE_ATTENDANCE_OF_A_USER_TAG,
    RECREATION_LEVEL_ATTENDANCES_TAG,
    RECREATION_SESSIONS_TAG,
    RECREATION_SESSION_ATTENDANCES_TAG,
    RECREATION_SESSION_INSTANCES_ATTENDANCE_TAG,
    RECREATION_SESSION_INSTANCES_TAG,
    RECREATION_SESSION_TAG,
    RECREATION_TAG,
    ROLES_TAG,
    SEASONS_TAG,
    SEASON_TAG,
    SINGLE_COMPETITION_TAG,
    SPONSORS_TAG,
    SPORTS_TAG,
    SPORT_TAG,
    STUDENT_STATUS_TAG,
    TEAMS_TAG,
    TEAM_MEMBERS_TAG,
    UNAPPLIED_RECREATION_SESSION_INSTANCES_TAG,
    UNIT_ROLES_AND_PERMISSIONS_TAG,
    USER,
    USERS_TAG,
    CMS_PUBLICATIONS_TAG,
    CMS_EVENTS_TAG,
    CMS_PAGES_TAG,
    CMS_DOCUMENT_CATEGORIES_TAG,
    CMS_SETTINGS_TAG,
    CMS_PUBLICATION_CATEGORIES_TAG,
} from './tags'

/** Safely parse a JSON-stringified value from storage. Returns empty string on failure. */
function parseStoredToken(raw: string | null): string {
    if (!raw) return ''
    try {
        const parsed = JSON.parse(raw)
        return typeof parsed === 'string' ? parsed : ''
    } catch {
        return raw
    }
}

export const baseApi = createApi({
    reducerPath: 'api',
    baseQuery: fetchBaseQuery({
        baseUrl: env.apiUrl,
        prepareHeaders: (headers, { getState }) => {
            const stateToken = (getState() as RootState)?.session?.token ?? ''

            const token =
                stateToken ||
                parseStoredToken(sessionStorage.getItem('accessToken')) ||
                parseStoredToken(localStorage.getItem('accessToken'))

            if (token) {
                headers.set('Authorization', `Bearer ${token}`)
            }
            headers.set('Content-Type', 'application/json')
            headers.set('Accept-Language', getLanguageFromStorage())

            return headers
        },
    }),

    tagTypes: [
        CLIENT_TAG,
        STUDENT_STATUS_TAG,
        MEDICAL_EXAM_TAG,
        USERS_TAG,
        USER,
        ROLES_TAG,
        ORGANIZATION_TAG,
        ORGANIZATION_UNIT_TAG,
        ORGANIZATION_UNIT_CONTACTS_TAG,
        ORGANIZATION_UNIT_OPTIONS,
        QUICK_SELECTS_TAG,
        SEASON_TAG,
        LEAGUE_TAG,
        LEAGUES_TAG,
        UNIT_ROLES_AND_PERMISSIONS_TAG,
        ACCREDITATION_TAG,
        SEASONS_TAG,
        SPONSORS_TAG,
        SPORTS_TAG,
        POSITION_TAG,
        FORMATION_TAG,
        SPORT_TAG,
        EVENT_TAG,
        DISCIPLINE_TAG,
        COMPETITION_TAG,
        APPLICATION_COMPETITION_TAG,
        COMPETITION_DOCUMENTS_TAG,
        GENERAL_APPLICATION,
        TEAM_MEMBERS_TAG,
        TEAMS_TAG,
        PHASES_TAG,
        GROUP_TAG,
        GROUP_MATCHES_TAG,
        RACE_STATS_TAG,
        COMPETITION_TABLE_STATS_TAG,
        SINGLE_COMPETITION_TAG,
        RACES_TAG,
        CHILD_MATCHES_TAG,
        INDIVIDUAL_USER,
        MATCHES_TAG,
        MATCH_TAG,
        RECREATIONS_TAG,
        RECREATION_TAG,
        RECREATION_SESSIONS_TAG,
        RECREATION_SESSION_TAG,
        COMPETITION_GROUP_STATS_TAG,
        RECREATION_SESSION_INSTANCES_TAG,
        RECREATION_LEVEL_ATTENDANCES_TAG,
        RECREATION_SESSION_ATTENDANCES_TAG,
        RECREATION_SESSION_INSTANCES_ATTENDANCE_TAG,
        UNAPPLIED_RECREATION_SESSION_INSTANCES_TAG,
        RECREATION_INSTANCE_ATTENDANCE_OF_A_USER_TAG,
        RECREATION_INSTANCE_ATTENDANCE_OF_A_RECREATION_TAG,
        CMS_PUBLICATIONS_TAG,
        CMS_EVENTS_TAG,
        CMS_PAGES_TAG,
        CMS_DOCUMENT_CATEGORIES_TAG,
        CMS_SETTINGS_TAG,
        CMS_PUBLICATION_CATEGORIES_TAG,
    ],
    endpoints: () => ({}),
})

// Re-exported from shared/lib/useLanguageRefetch — this hook was moved there.
// Keep this re-export for backwards compatibility.
export { useLanguageRefetch } from '../lib/useLanguageRefetch/useLanguageRefetch'
